// Ticket 18 — shared per-measure detection-rate bar chart used by the
// Teams view team / assessment cards. Ticket 19 reuses it on
// AgentProfileView. Task #197 extends each bar with two inline
// comparison markers (prior period + cross-team average) so a
// manager can triage at a glance without hovering for tooltips. The
// extra props are optional so callers that haven't migrated render
// identically to today.
//
// Task #260 — the prior-period and team-average tick marks are now
// self-explanatory: each marker is wrapped in the shared ``Tooltip``
// primitive (label name + comparison series name + exact %) and is
// keyboard-reachable. The "Prior period" / "Team average" legend is
// rendered above the first measure group (was below) and the bottom
// copy was removed so it isn't duplicated.
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { AggregateRow } from "@/lib/api";
import { Tooltip } from "@/components/Tooltip";
import { LabelWithTooltip } from "@/components/MetricTooltips";

// Task #372 — per-measure-group collapse state is persisted in
// localStorage under a caller-scoped prefix so a user's open/closed
// choice survives reload. Keyed by ``${prefix}:${measure_id}`` to
// keep different chart instances (different agent, team, assessment)
// independent.
const MEASURE_GROUP_STORAGE_PREFIX = "measure-bar-chart:group:";

function readGroupStored(
  storageKey: string | undefined,
  measureId: string,
  fallback: boolean,
): boolean {
  if (!storageKey) return fallback;
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(
      `${MEASURE_GROUP_STORAGE_PREFIX}${storageKey}:${measureId}`,
    );
    if (raw === "1") return true;
    if (raw === "0") return false;
  } catch {
    // ignore — localStorage may be unavailable
  }
  return fallback;
}

function writeGroupStored(
  storageKey: string,
  measureId: string,
  value: boolean,
) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${MEASURE_GROUP_STORAGE_PREFIX}${storageKey}:${measureId}`,
      value ? "1" : "0",
    );
  } catch {
    // ignore
  }
}

export type MeasureFocus = {
  measure_id: string;
  label_name: string;
};

export function MeasureBarChart({
  rows,
  callCount,
  priorRows,
  teamAverageRows,
  focused,
  onFocus,
  measureDescriptions,
  groupStorageKey,
}: {
  rows: AggregateRow[];
  callCount: number;
  // Task #197 — optional comparison series. ``priorRows`` is the
  // same shape as ``rows`` but for the prior period; matching pairs
  // are joined by ``"${measure_id}::${label_name}"``. Same for
  // ``teamAverageRows`` (cross-team average per (measure,label)).
  priorRows?: AggregateRow[];
  teamAverageRows?: AggregateRow[];
  // Optional row focus — when set, the matching bar is highlighted
  // and ``onFocus`` lets the parent drive an inline agent drill on
  // that exact (measure, label_name) pair.
  focused?: MeasureFocus | null;
  onFocus?: (focus: MeasureFocus | null) => void;
  // Task #316 — optional per-measure description map keyed by
  // ``measure_id``. When a custom user-defined measure has a
  // description stored on the assessment definition, threading it
  // through here surfaces an info tooltip next to that measure's
  // heading; measures without a description render plainly.
  measureDescriptions?: Record<string, string | null | undefined>;
  // Task #372 — when set, each measure group's open/closed state is
  // persisted in localStorage under this caller-scoped prefix so the
  // user's choice survives reloads. Omit (or pass undefined) and the
  // chart still renders collapsible groups, just without persistence.
  // Scope per-instance (e.g. include an agent / team / assessment id)
  // so toggling one chart doesn't affect another.
  groupStorageKey?: string;
}) {
  // Group by measure_id, preserving the server's measure ordering so the
  // first measure encountered renders first.
  const byMeasure = new Map<string, { label: string; rows: AggregateRow[] }>();
  for (const r of rows) {
    if (!byMeasure.has(r.measure_id)) {
      byMeasure.set(r.measure_id, { label: r.measure_label, rows: [] });
    }
    byMeasure.get(r.measure_id)!.rows.push(r);
  }

  // Build O(1) lookup maps for the comparison series so we don't
  // re-scan ``priorRows`` / ``teamAverageRows`` for every bar.
  const priorMap = new Map<string, number>();
  for (const r of priorRows ?? []) {
    priorMap.set(`${r.measure_id}::${r.label_name}`, r.detection_rate);
  }
  const avgMap = new Map<string, number>();
  for (const r of teamAverageRows ?? []) {
    avgMap.set(`${r.measure_id}::${r.label_name}`, r.detection_rate);
  }

  const hasComparisons = priorMap.size > 0 || avgMap.size > 0;
  const interactive = !!onFocus;

  // Task #372 — per-measure-group collapse state. Each group defaults
  // to *closed* so the chart loads as a compact list of group
  // headings the user can expand individually. State is a Set of
  // collapsed measure_ids; a stored "1" flips a group back to open
  // for users who have already expanded it. When ``groupStorageKey``
  // is supplied, the per-group choice is hydrated from / persisted to
  // localStorage so it survives reloads.
  const measureIds = Array.from(byMeasure.keys());
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    for (const id of measureIds) {
      if (!readGroupStored(groupStorageKey, id, false)) initial.add(id);
    }
    return initial;
  });
  // measureIds is a fresh array every render; this stable key lets the effect
  // below react to the *set* of ids changing without firing every render.
  const measureIdsKey = measureIds.join("|");
  const hydratedKeyRef = useRef<string | undefined>(groupStorageKey);
  const knownIdsRef = useRef<Set<string>>(new Set(measureIds));
  useEffect(() => {
    // (1) storageKey changed (e.g. navigating between agents / teams):
    // full rehydrate so the new instance picks up its own stored choices
    // instead of inheriting the previous entity's set.
    if (hydratedKeyRef.current !== groupStorageKey) {
      hydratedKeyRef.current = groupStorageKey;
      knownIdsRef.current = new Set(measureIds);
      const next = new Set<string>();
      for (const id of measureIds) {
        if (!readGroupStored(groupStorageKey, id, false)) next.add(id);
      }
      setClosedGroups(next);
      return;
    }
    // (2) same storageKey but a measure appeared after mount (data refetch
    // with a new measure_id): default it to closed per the group contract,
    // honouring any stored choice, without disturbing in-session toggles
    // for existing groups.
    const fresh: string[] = [];
    for (const id of measureIds) {
      if (!knownIdsRef.current.has(id)) {
        knownIdsRef.current.add(id);
        fresh.push(id);
      }
    }
    if (fresh.length === 0) return;
    setClosedGroups((cur) => {
      const next = new Set(cur);
      for (const id of fresh) {
        if (!readGroupStored(groupStorageKey, id, false)) next.add(id);
      }
      return next;
    });
    // measureIdsKey is the stable trigger for the id-set changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupStorageKey, measureIdsKey]);
  const toggleGroup = (measureId: string) => {
    setClosedGroups((cur) => {
      const next = new Set(cur);
      const willBeOpen = next.has(measureId);
      if (willBeOpen) next.delete(measureId);
      else next.add(measureId);
      if (groupStorageKey) writeGroupStored(groupStorageKey, measureId, willBeOpen);
      return next;
    });
  };

  if (callCount === 0 || byMeasure.size === 0) {
    // Task #188 — keep a stable height so switching the top-of-page
    // range between Daily (often zero graded calls) and Weekly doesn't
    // collapse the card. The wrapper mirrors the rough vertical space
    // a populated single-measure chart would take. NOTE: this early
    // return lives *after* the hooks above so the hook call order stays
    // stable across renders (react-hooks/rules-of-hooks).
    return (
      <div
        className="flex items-center justify-center rounded border border-dashed border-border bg-muted/20 text-sm text-muted-foreground italic"
        style={{ minHeight: 120 }}
      >
        No assessments graded in this period.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hasComparisons ? (
        <div
          className="flex items-center gap-4 text-[11px] text-muted-foreground"
          data-testid="measure-bar-chart-legend"
        >
          {priorMap.size > 0 ? (
            <LabelWithTooltip
              metricId="prior_period"
              className="inline-flex items-center gap-1.5"
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-px bg-muted-foreground/70"
                  aria-hidden="true"
                />
                Prior period
              </span>
            </LabelWithTooltip>
          ) : null}
          {avgMap.size > 0 ? (
            <LabelWithTooltip
              metricId="team_average"
              className="inline-flex items-center gap-1.5"
            >
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-3 w-px bg-accent"
                  aria-hidden="true"
                />
                Team average
              </span>
            </LabelWithTooltip>
          ) : null}
        </div>
      ) : null}
      {Array.from(byMeasure.entries()).map(([measureId, m]) => {
        const isOpen = !closedGroups.has(measureId);
        const contentId = `measure-bar-group-${measureId}`;
        return (
        <div key={measureId}>
          {/* Task #372 — measure-group header is now a button so the
              group can be collapsed independently. The chevron rotates
              to mirror other collapsible surfaces (CollapsibleSection,
              team drill rows). The LabelWithTooltip stays nested so
              the info tooltip still works when a custom measure has a
              description. */}
          <button
            type="button"
            onClick={() => toggleGroup(measureId)}
            aria-expanded={isOpen}
            aria-controls={contentId}
            className="w-full flex items-center justify-between gap-2 mb-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <LabelWithTooltip
                description={measureDescriptions?.[measureId] ?? null}
                className="inline-flex items-center gap-1"
              >
                {m.label}
              </LabelWithTooltip>
            </div>
            <ChevronDown
              aria-hidden="true"
              className={`h-4 w-4 text-muted-foreground transition-transform duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
                isOpen ? "rotate-0" : "-rotate-90"
              }`}
            />
          </button>
          {isOpen && (
          <div id={contentId} className="space-y-1.5">
            {m.rows.map((r) => {
              const pct = Math.round(r.detection_rate * 100);
              const key = `${r.measure_id}::${r.label_name}`;
              const prior = priorMap.get(key);
              const avg = avgMap.get(key);
              const priorPct = prior !== undefined ? Math.round(prior * 100) : null;
              const avgPct = avg !== undefined ? Math.round(avg * 100) : null;
              const isFocused =
                !!focused &&
                focused.measure_id === r.measure_id &&
                focused.label_name === r.label_name;
              const bar = (
                <div
                  className={`flex items-center gap-3 rounded ${
                    interactive
                      ? "cursor-pointer hover:bg-muted/40 px-2 -mx-2 py-1 -my-1"
                      : ""
                  } ${isFocused ? "bg-primary/5 ring-1 ring-primary/30" : ""}`}
                  data-focused={isFocused ? "true" : undefined}
                >
                  <div className="w-40 shrink-0 truncate text-xs text-foreground">
                    {r.label_name}
                  </div>
                  <div className="relative flex-1 h-5">
                    {/* The filled bar lives inside its own overflow-hidden
                        wrapper so its rounded corners clip the primary
                        fill, while the marker tooltips (siblings below)
                        can still pop out of the bar without being
                        clipped. */}
                    <div className="absolute inset-0 bg-muted rounded overflow-hidden">
                      <div
                        className="h-full bg-primary transition-[width] duration-[var(--duration-base)] ease-[var(--easing-standard)]"
                        style={{ width: `${pct}%` }}
                        aria-label={`${r.label_name} ${pct}%`}
                      />
                    </div>
                    {priorPct !== null ? (
                      <Tooltip
                        content={`Prior period — ${r.label_name}: ${priorPct}%`}
                        wrapperClassName=""
                        wrapperStyle={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: `${priorPct}%`,
                          width: "1px",
                        }}
                        data-testid="measure-bar-tooltip-prior"
                      >
                        <span
                          tabIndex={0}
                          role="img"
                          className="block h-full w-full bg-muted-foreground/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`Prior ${r.label_name} ${priorPct}%`}
                          data-marker="prior"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              e.stopPropagation();
                          }}
                        />
                      </Tooltip>
                    ) : null}
                    {avgPct !== null ? (
                      <Tooltip
                        content={`Team average — ${r.label_name}: ${avgPct}%`}
                        wrapperClassName=""
                        wrapperStyle={{
                          position: "absolute",
                          top: 0,
                          bottom: 0,
                          left: `${avgPct}%`,
                          width: "1px",
                        }}
                        data-testid="measure-bar-tooltip-team-average"
                      >
                        <span
                          tabIndex={0}
                          role="img"
                          className="block h-full w-full bg-accent border-l border-dashed border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`Team average ${r.label_name} ${avgPct}%`}
                          data-marker="team-average"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ")
                              e.stopPropagation();
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </div>
                  <div className="w-20 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {pct}%
                  </div>
                </div>
              );
              return interactive ? (
                <button
                  key={r.label_name}
                  type="button"
                  className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  aria-pressed={isFocused}
                  onClick={() =>
                    onFocus!(
                      isFocused
                        ? null
                        : { measure_id: r.measure_id, label_name: r.label_name },
                    )
                  }
                >
                  {bar}
                </button>
              ) : (
                <div key={r.label_name}>{bar}</div>
              );
            })}
          </div>
          )}
        </div>
        );
      })}
    </div>
  );
}
